import { throwError } from "../../lib/throwError";
import { zulipAxiosConfig } from "../axiosConfig";
import * as qs from "qs";

interface DMUserListPayload {
  anchor: number | string;
  num_before: number;
  num_after: number;
  client_gravatar: boolean;
  allow_empty_topic_name: boolean;
  narrow?: Array<{
    negated: boolean;
    operator: string;
    operand: number[];
  }>;
}

interface SendMessagePayload {
  type: string;
  to: (string | number)[];
  content: string;
  topic?: string;
}

interface CreateChannelPayload {
  schemaName: string;
  payload: any;
}

class ZulipServices {
  getUsers = async (): Promise<any> => {
    return await zulipAxiosConfig.get("/users");
  };

  getChannelList = async (): Promise<any> => {
    return await zulipAxiosConfig.get("/streams");
  };

  getUsersByChannel = async (channelId: string): Promise<any> => {
    return await zulipAxiosConfig.get(`/streams/${channelId}/members`);
  };

  getDMUserList = async ({
    DMUserListPayload,
  }: {
    DMUserListPayload: DMUserListPayload;
  }) => {
    return await zulipAxiosConfig.get("/messages", {
      params: DMUserListPayload,
      paramsSerializer: params => {
        return qs.stringify(params, {
          encode: false,
          arrayFormat: "repeat",
        });
      },
    });
  };
  getDirectMessage = async ({ payload }: any): Promise<any> => {
    return await zulipAxiosConfig.get("/messages", {
      params: payload,
      paramsSerializer: params => {
        return qs.stringify(params, {
          encode: false,
          arrayFormat: "repeat",
        });
      },
    });
  };

  getChatOfChannel = async ({ chatOfChannelPayload }: any): Promise<any> => {
    return await zulipAxiosConfig.get("/messages", {
      params: chatOfChannelPayload,
      paramsSerializer: params => {
        return qs.stringify(params, {
          encode: false,
          arrayFormat: "repeat",
        });
      },
    });
  };

  createChannels = async ({
    schemaName,
    payload,
  }: CreateChannelPayload): Promise<any> => {
    if (!schemaName || !payload) {
      throwError("Schema name and payload are required", "ZulipServices");
    }
    return await zulipAxiosConfig.post("/users/me/subscriptions", payload);
  };

  sendDirectMessage = async (payload: SendMessagePayload): Promise<any> => {
    if (!payload) {
      throwError("Payload is required", "ZulipServices");
    }

    const params = new URLSearchParams();
    params.append("type", payload.type);
    params.append("to", JSON.stringify(payload.to));
    params.append("content", payload.content);

    return await zulipAxiosConfig.post("/messages", params, {
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
    });
  };

  sendChannelMessage = async (payload: SendMessagePayload): Promise<any> => {
    if (!payload) {
      throwError("Payload is required", "ZulipServices");
    }

    const formData = new FormData();
    formData.append("type", payload.type);
    formData.append("to", JSON.stringify(payload.to));
    formData.append("topic", JSON.stringify(payload.topic ?? ""));
    formData.append("content", payload.content);

    return await zulipAxiosConfig.post("/messages", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  };

  uploadFile = async (file: File): Promise<any> => {
    const formData = new FormData();
    formData.append("filename", file);

    return await zulipAxiosConfig.post("/user_uploads", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  };

  deleteMessage = async (messageId: number): Promise<any> => {
    return await zulipAxiosConfig.delete(`/messages/${messageId}`);
  };

  addReaction = async (messageId: number, emojiName: string): Promise<any> => {
    const params = new URLSearchParams();
    params.append("emoji_name", emojiName);
    return await zulipAxiosConfig.post(`/messages/${messageId}/reactions`, params, {
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
    });
  };

  removeReaction = async (messageId: number, emojiName: string): Promise<any> => {
    return await zulipAxiosConfig.delete(`/messages/${messageId}/reactions`, {
      params: { emoji_name: emojiName },
    });
  };

  getCustomEmojis = async (): Promise<any> => {
    return await zulipAxiosConfig.get("/realm/emoji");
  };
}

const instance = new ZulipServices();
export default instance;
